import type {
  AgenticCartBundleSummaryResponse,
  CreateAgenticCartOrderResponse,
} from "@ishvaram/api-client";
import { getGoogleAnalyticsClientId } from "@/lib/analytics-ga4";
import { abandonPayment } from "@/lib/api/plans";
import { reportJsError } from "@/lib/js-error-beacon";
import {
  buildCartCheckoutRecoveryContext,
  buildCartRecoverySupportEmailHref,
  clearCartCheckoutRecoveryContext,
  saveCartCheckoutRecoveryContext,
} from "@/lib/cart-checkout-recovery";
import {
  AGENTIC_CART_CHECKOUT_CHANNEL,
  AGENTIC_CART_COMMERCE_SYSTEM,
  AGENTIC_CART_PLACEMENT,
  AGENTIC_CART_PLAN_TYPE,
  AGENTIC_CART_SOURCE,
} from "./AgenticCartCheckout.attribution";
import type { CartRecoveryState, ContactForm } from "./AgenticCartCheckout.types";

type AbandonContext = {
  bundle: AgenticCartBundleSummaryResponse;
  order: CreateAgenticCartOrderResponse;
  contact: ContactForm;
  sourcePath: string;
  intent: string;
  customerProblem: string;
  journeyActionId?: string;
  concernId?: string;
  language?: "en" | "hi";
  /** Pre-checked WhatsApp order/delivery opt-in from the checkout box. */
  whatsappConsent?: boolean;
};

/**
 * Mirror the report-CTA recovery: persist an on-site resume context, surface a
 * one-tap resume + support affordance, and notify the backend so the proven
 * report-abandon recovery pathway (recovery email) picks this up. The recovery
 * URL is the same slug-parameterized cart screen — there is no per-cart route.
 */
export function recordAgenticCartAbandon(
  ctx: AbandonContext,
  onRecovery: (state: CartRecoveryState) => void
): void {
  const email = ctx.contact.email.trim();
  const phone = ctx.contact.phone.trim();
  const recovery = buildCartCheckoutRecoveryContext({
    bundleId: ctx.bundle.bundle_id,
    bundleName: ctx.bundle.name,
    priceInr: ctx.bundle.price_inr,
    cartId: ctx.order.cart_id,
    razorpayOrderId: ctx.order.razorpay_order_id,
    sourcePath: ctx.sourcePath,
    intent: ctx.intent,
    customerProblem: ctx.customerProblem,
    journeyActionId: ctx.journeyActionId,
    concernId: ctx.concernId,
    language: ctx.language,
    customerEmail: email || undefined,
    customerPhone: phone || undefined,
  });
  saveCartCheckoutRecoveryContext(recovery);
  onRecovery({
    resumeHref: recovery.resumeHref,
    supportHref: buildCartRecoverySupportEmailHref(recovery),
    bundleName: recovery.bundleName,
  });
  // Route through the existing report-plan abandon pathway (recovery email):
  // the bundle's report_plan_id is the recoverable value, and the native-cart
  // channel keeps it attributable. Fire-and-forget; never block the UI.
  void abandonPayment({
    razorpay_order_id: ctx.order.razorpay_order_id,
    plan_id: ctx.bundle.report_plan_id,
    plan_type: AGENTIC_CART_PLAN_TYPE,
    email: email || undefined,
    phone: phone || undefined,
    source: AGENTIC_CART_SOURCE,
    placement: AGENTIC_CART_PLACEMENT,
    checkout_channel: AGENTIC_CART_CHECKOUT_CHANNEL,
    commerce_system: AGENTIC_CART_COMMERCE_SYSTEM,
    intent: ctx.intent,
    source_path: ctx.sourcePath,
    customer_problem: ctx.customerProblem,
    journey_action_id: ctx.journeyActionId,
    concern_id: ctx.concernId,
    recovery_channel: "onsite",
    recovery_url: recovery.resumeHref,
    recovery_email_consent: Boolean(email),
    // WhatsApp recovery opt-in — true only when the pre-checked box stayed
    // checked AND a phone is present (native cart always collects one).
    recovery_phone_consent: Boolean(ctx.whatsappConsent) && Boolean(phone),
    ga_client_id: getGoogleAnalyticsClientId(),
  }).catch((err) =>
    reportJsError({
      error_message: err instanceof Error ? err.message : "abandonPayment failed",
      error_name: "AbandonPaymentFailed",
      boundary: "agentic_cart_abandon",
    })
  );
}

export { clearCartCheckoutRecoveryContext };
